import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { AiCompletionService } from '../ai-completion.service';

@Directive({
    selector: '[rexFloatButton]'
})
export class RexFloatButtonDirective implements AfterViewInit, OnDestroy {
    @Input('rexFloatButton') title: string | undefined;
    @Input() rexSubtitle: string | undefined;

    private buttonEl: HTMLElement | null = null;
    private labelEl: HTMLElement | null = null;
    private cleanupFns: Array<() => void> = [];
    private sub: Subscription | null = null;

    constructor(private elRef: ElementRef<HTMLElement>, private renderer: Renderer2, private zone: NgZone, private ai: AiCompletionService) { }

    ngAfterViewInit(): void {
        const host = this.getInputElement();
        if (!host) { return; }

        // Ensure parent is positioned to place absolute button
        const parent = host.parentElement as HTMLElement;
        if (parent && getComputedStyle(parent).position === 'static') {
            this.renderer.setStyle(parent, 'position', 'relative');
        }

        this.createButton(parent || host);
        this.updateButtonVisibility();
    }

    ngOnDestroy(): void {
        this.cleanup();
        if (this.sub) { this.sub.unsubscribe(); }
    }

    private getInputElement(): HTMLInputElement | HTMLTextAreaElement | null {
        const native = this.elRef.nativeElement as HTMLElement;
        if (native instanceof HTMLInputElement || native instanceof HTMLTextAreaElement) { return native; }
        const input = native.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null;
        return input;
    }

    private createButton(container: HTMLElement): void {
        if (this.buttonEl) { return; }
        const btn = this.renderer.createElement('button');
        this.renderer.setAttribute(btn, 'type', 'button');
        this.renderer.addClass(btn, 'rex-float-btn');
        this.renderer.setStyle(btn, 'position', 'absolute');
        this.renderer.setStyle(btn, 'right', '8px');
        this.renderer.setStyle(btn, 'bottom', '8px');
        this.renderer.setStyle(btn, 'padding', '4px 8px');
        this.renderer.setStyle(btn, 'display', 'none');
        this.renderer.setStyle(btn, 'alignItems', 'center');
        this.renderer.setStyle(btn, 'gap', '6px');
        this.renderer.setStyle(btn, 'borderRadius', '6px');
        this.renderer.setStyle(btn, 'border', '1px solid #e0e0e0');
        this.renderer.setStyle(btn, 'background', '#fff');
        this.renderer.setStyle(btn, 'boxShadow', '0 2px 6px rgba(0,0,0,0.08)');
        this.renderer.setStyle(btn, 'cursor', 'pointer');
        this.renderer.setStyle(btn, 'zIndex', '3');
        this.renderer.setStyle(btn, 'fontSize', '12px');

        const img = this.renderer.createElement('img');
        this.renderer.setAttribute(img, 'src', 'assets/img/rex.png');
        this.renderer.setAttribute(img, 'alt', 'Rex');
        this.renderer.setStyle(img, 'height', '16px');
        this.renderer.setStyle(img, 'width', '16px');
        this.renderer.setStyle(img, 'marginRight', '6px');
        const label = this.renderer.createElement('span');
        const text = this.renderer.createText('Generar con la IA Rex ✨');
        this.renderer.appendChild(label, text);
        this.labelEl = label;
        this.renderer.appendChild(btn, img);
        this.renderer.appendChild(btn, label);

        this.renderer.appendChild(container, btn);
        this.buttonEl = btn;

        const offClick = this.renderer.listen(btn, 'click', () => this.generateWithRex());
        this.cleanupFns.push(offClick);

        // host events to toggle visibility
        const host = this.getInputElement();
        if (host) {
            /* const offFocus = this.renderer.listen(host, 'focus', () => this.updateButtonVisibility()); */
            const offInput = this.renderer.listen(host, 'input', () => this.updateButtonVisibility());
            const offBlur = this.renderer.listen(host, 'blur', () => {
                setTimeout(() => this.updateButtonVisibility(), 150);
            });
            this.cleanupFns.push(offInput, offBlur);

/*             this.cleanupFns.push(offFocus, offInput, offBlur);
 */        }
    }

    private updateButtonVisibility(): void {
        const host = this.getInputElement();
        if (!host || !this.buttonEl) { return; }
        const hasValue = (host.value || '').trim().length > 0;
        const isFocused = document.activeElement === host;
        const shouldShow = isFocused || hasValue;
        this.renderer.setStyle(this.buttonEl, 'display', shouldShow ? 'inline-flex' : 'none');
    }

    private setGenerating(isGenerating: boolean): void {
        if (!this.buttonEl || !this.labelEl) { return; }
        if (isGenerating) {
            this.renderer.setProperty(this.labelEl, 'innerText', 'Generando con la IA Rex ✨...');
            this.renderer.setStyle(this.buttonEl, 'opacity', '0.85');
            this.renderer.setStyle(this.buttonEl, 'pointerEvents', 'none');
        } else {
            this.renderer.setProperty(this.labelEl, 'innerText', 'Generar con la IA Rex ✨');
            this.renderer.removeStyle(this.buttonEl, 'opacity');
            this.renderer.removeStyle(this.buttonEl, 'pointerEvents');
        }
    }

    private generateWithRex(): void {
        const host = this.getInputElement();
        if (!host) { return; }
        const context = host.value || '';
        if (!context || context.trim().length < 5) {
            // Require a bit of contexto para mejores resultados
            return;
        }
        const systemPrompt = 'Eres un asistente que responde en español. Mejora y completa el siguiente texto para que sea claro, conciso y persuasivo. Devuelve solo el texto final, sin títulos ni listas.';
        this.zone.run(() => {
            this.setGenerating(true);
            if (this.sub) { this.sub.unsubscribe(); }
            this.sub = this.ai.generateCompletion(context, systemPrompt).subscribe({
                next: (texto: string) => {
                    const finalText = (texto || '').trim();
                    if (!finalText) { return; }
                    host.value = finalText;
                    const evt = new Event('input', { bubbles: true });
                    host.dispatchEvent(evt);
                    this.updateButtonVisibility();
                },
                error: () => {
                    if (this.labelEl) {
                        this.renderer.setProperty(this.labelEl, 'innerText', 'Reintentar');
                    }
                },
                complete: () => {
                    this.setGenerating(false);
                }
            });
        });
    }

    private cleanup(): void {
        for (const off of this.cleanupFns) { try { off(); } catch { } }
        this.cleanupFns = [];
        if (this.buttonEl && this.buttonEl.parentElement) {
            this.buttonEl.parentElement.removeChild(this.buttonEl);
        }
        this.buttonEl = null;
    }
}


